// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import React, { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { StageTransition } from "@/hooks/api/use-account-opportunities";
import type { Person } from "@/hooks/api/use-account-persons";
import { formatTimestampShort } from "@/lib/date-formatting";

export type OutcomeType = "won" | "lost" | "active";

/**
 * Helper function to determine the CSS class for a stage pill in opportunity tooltips
 *
 * @param stage Current stage being displayed
 * @param currentStage The opportunity's current stage
 * @param isOutcome Whether this is an outcome stage
 * @param isLastStage Whether this is the last stage in the display sequence
 * @returns CSS class string for the stage pill
 */
function getStageClassNames(
  stage: string,
  currentStage: string,
  isOutcome: boolean,
  isLastStage: boolean,
): string {
  // Highlight the stage if:
  // 1. It's an outcome and this is the last stage displayed, or
  // 2. It's not an outcome and this stage matches the opportunity's current stage
  const isHighlighted =
    (isOutcome && isLastStage) || (!isOutcome && stage === currentStage);

  return isHighlighted ? "bg-primary/20 text-primary font-medium" : "bg-muted";
}

// Define a custom stage transition type that matches what's used in the chart
interface ChartStageTransition {
  stage: string;
  timestamp: number;
}

// Define a union type for stage transitions
type StageTransitionType = ChartStageTransition | StageTransition;

// Original Interface for data passed by Recharts Tooltip
// (Kept for reference, but OpportunityTooltip/ActivityTooltip will use TooltipProps again)
interface OpportunityData {
  id: string;
  name: string;
  stage: string;
  outcome: OutcomeType;
  stageTransitions: ChartStageTransition[] | StageTransition[];
  y: number;
}
type OpportunityTooltipData =
  | OpportunityData
  | {
      id: string;
      name: string;
      stage: string;
      stageTransitions?: ChartStageTransition[] | StageTransition[];
      y: number;
      entry?: unknown; // For progression opportunities
    };

// Recharts Tooltip Prop Type (used by reverted components)
export interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    name: string;
    value: number;
    payload: {
      date?: string;
      count?: number;
      opportunities?: OpportunityData[]; // Used by original OpportunityTooltip
      outcomeOpportunities?: OpportunityData[]; // Used by original OpportunityTooltip
      people?: Person[]; // Used by original PersonTooltip (though PersonTooltip now uses direct props)
      timestamp?: number;
    };
  }>;
  label?: string | number; // Timestamp
}

// Props for the working PersonTooltip (remains unchanged)
export interface PersonTooltipProps {
  people: Person[] | null;
  timestamp: number | null;
  position: { x: number; y: number } | null;
}

// Props for reverted ActivityTooltip (now uses TooltipProps)
// export interface ActivityTooltipProps { ... } // Removed specific props

// Props for reverted OpportunityTooltip (now uses TooltipProps)
// export interface OpportunityTooltipProps { ... } // Removed specific props

// --- Positioning Logic for PersonTooltip (remains unchanged) ---
const tooltipOffset = { x: 0, y: -25 };
const useAdjustedPosition = (
  position: { x: number; y: number } | null,
  tooltipRef: React.RefObject<HTMLDivElement>,
) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [finalPosition, setFinalPosition] = useState({ left: 0, top: 0 });
  const [isMeasured, setIsMeasured] = useState(false);

  useEffect(() => {
    setIsMeasured(false);
  }, []);
  useEffect(() => {
    if (tooltipRef.current && !isMeasured && position) {
      const { width, height } = tooltipRef.current.getBoundingClientRect();
      setDimensions({ width, height });
      setIsMeasured(true);
    }
  }, [tooltipRef, isMeasured, position]);
  useEffect(() => {
    if (!position || !isMeasured) return;
    const { width: tooltipWidth, height: tooltipHeight } = dimensions;
    const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;
    let x = position.x - tooltipWidth / 2 + tooltipOffset.x;
    let y = position.y - tooltipHeight + tooltipOffset.y;
    if (x + tooltipWidth > viewportWidth - 20)
      x = viewportWidth - tooltipWidth - 20;
    if (x < 20) x = 20;
    if (y < 20) y = position.y + 20;
    if (y + tooltipHeight > viewportHeight - 20)
      y = viewportHeight - tooltipHeight - 20;
    setFinalPosition({ left: x, top: y });
  }, [position, dimensions, isMeasured]);
  return { finalPosition, isMeasured };
};
const BaseTooltip = ({
  position,
  children,
}: {
  position: { x: number; y: number } | null;
  children: React.ReactNode;
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { finalPosition, isMeasured } = useAdjustedPosition(
    position,
    tooltipRef,
  );
  if (!position) return null;
  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed bg-card text-card-foreground rounded shadow-lg text-xs whitespace-nowrap z-[1000] p-3 animate-in fade-in zoom-in-95 duration-150 border border-border backdrop-blur-sm backdrop-saturate-125"
      style={{
        maxWidth: "350px",
        pointerEvents: "none",
        transition: isMeasured ? "left 0.1s, top 0.1s" : "none",
        ...(isMeasured
          ? { left: `${finalPosition.left}px`, top: `${finalPosition.top}px` }
          : {
              opacity: 0,
              left: `${position.x - 150}px`,
              top: `${position.y - 50}px`,
            }),
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
// --- End Positioning Logic ---

// --- Tooltip Components ---

// REVERTED OpportunityTooltip - uses TooltipProps from Recharts
export const OpportunityTooltip = ({
  active,
  payload,
  label,
}: TooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  // Original logic to extract opportunities from payload
  const hasOutcomeData = payload.some(
    (p) =>
      p.name === "opportunity_outcome" &&
      p.payload.outcomeOpportunities?.length,
  );
  const allOpportunities: Array<{
    id: string;
    name: string;
    y: number;
    isOutcome: boolean;
    data: OpportunityTooltipData;
  }> = [];
  const uniqueOpportunityNames = new Set<string>();
  if (hasOutcomeData) {
    const outcomeEntry = payload.find(
      (entry) =>
        entry.name === "opportunity_outcome" &&
        entry.payload.outcomeOpportunities,
    );
    if (outcomeEntry?.payload.outcomeOpportunities) {
      const uniqueOutcomeOpps =
        outcomeEntry.payload.outcomeOpportunities.reduce(
          (acc, opp) => {
            if (!acc[opp.name]) acc[opp.name] = opp;
            return acc;
          },
          {} as Record<
            string,
            (typeof outcomeEntry.payload.outcomeOpportunities)[0]
          >,
        );
      Object.values(uniqueOutcomeOpps).forEach((opp) => {
        if (!uniqueOpportunityNames.has(opp.name)) {
          uniqueOpportunityNames.add(opp.name);
          allOpportunities.push({
            id: opp.id,
            name: opp.name,
            y: opp.y,
            isOutcome: true,
            data: opp,
          });
        }
      });
    }
  }
  payload.forEach((entry) => {
    if (entry.name !== "opportunity_outcome" && entry.payload.opportunities) {
      const matchingOpp = entry.payload.opportunities.find(
        (opp) => opp.name === entry.name,
      );
      if (matchingOpp && !uniqueOpportunityNames.has(matchingOpp.name)) {
        uniqueOpportunityNames.add(matchingOpp.name);
        allOpportunities.push({
          id: matchingOpp.id,
          name: matchingOpp.name,
          y: matchingOpp.y,
          isOutcome: false,
          data: { ...matchingOpp, entry },
        });
      }
    }
  });
  allOpportunities.sort((a, b) => b.y - a.y);

  const timestamp = label as number;
  const exactDate = new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Original Rendering Logic (NO Portal, NO BaseTooltip)
  return (
    <div
      className="bg-card text-card-foreground p-3 shadow-lg rounded-lg border border-border backdrop-blur-sm backdrop-saturate-125 relative z-50"
      // Apply original inline styles if any (e.g., transform, margin)
      style={{ transform: "translateY(20px)", marginTop: "10px" }}
    >
      <p className="text-sm font-medium mb-1">{exactDate}</p>
      <div className="space-y-1 max-h-[250px] overflow-y-auto">
        {allOpportunities.map((opportunity, index) => {
          const isOutcome = opportunity.isOutcome;
          const oppData = opportunity.data as OpportunityData; // Simplified type cast
          const stageProgression = oppData.stageTransitions
            ?.map((transition: StageTransitionType) => {
              if ("stage" in transition) return transition.stage;
              if ("event_name" in transition)
                return transition.event_name.replace("Changed stage to ", "");
              return "Unknown Stage";
            })
            .filter(Boolean) || [oppData.stage];
          const displayStages =
            stageProgression.length > 0
              ? stageProgression
              : oppData.stage
                ? [oppData.stage]
                : [];

          return (
            <div
              key={`opp-${opportunity.id}-${index}`}
              className="border-t border-border mt-2 pt-2 first:border-t-0 first:mt-0 first:pt-0"
            >
              <div className="text-sm mt-1 space-y-1">
                <div className="font-medium">{oppData.name}</div>
                <div className="flex flex-col gap-0.5">
                  <div className="text-sm text-muted-foreground mt-1">
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {displayStages.map((stage: string, i: number) => (
                        <React.Fragment key={`stage-${i}`}>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${getStageClassNames(
                              stage,
                              oppData.stage,
                              isOutcome,
                              i === displayStages.length - 1,
                            )}`}
                          >
                            {stage}
                          </span>
                          {i < displayStages.length - 1 && (
                            <span className="text-muted-foreground">→</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  {isOutcome && oppData.outcome !== "active" && (
                    <p
                      className={`text-xs font-medium ${
                        oppData.outcome === "won"
                          ? "text-success"
                          : "text-destructive"
                      }`}
                    >
                      {oppData.outcome === "won" ? "Won" : "Lost"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
// OpportunityTooltip.displayName = "OpportunityTooltip"; // Optional

// PersonTooltip (Remains UNCHANGED - Uses Portal & BaseTooltip)
export const PersonTooltip = memo(
  ({ people, timestamp, position }: PersonTooltipProps) => {
    if (!people || people.length === 0 || !timestamp) return null;
    const exactDate = new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return (
      <BaseTooltip position={position}>
        <p className="text-sm font-medium mb-1">
          First touchpoint{people.length > 1 ? "s" : ""} on {exactDate}
        </p>
        <div className="space-y-1 max-h-[250px] overflow-y-auto">
          {people.map((person) => (
            <div
              key={`person-${person.id}`}
              className="border-t border-border mt-2 pt-2 first:border-t-0 first:mt-0 first:pt-0"
            >
              <div className="flex items-center justify-between">
                <p className="font-medium">
                  {[person.first_name, person.last_name]
                    .filter(Boolean)
                    .join(" ") ||
                    person.email_address ||
                    `ID: ${person.id.substring(0, 6)}...`}
                </p>
              </div>
              {person.job_title && (
                <p className="text-sm text-muted-foreground">
                  {person.job_title}
                </p>
              )}
              {person.email_address && (
                <p className="text-sm text-muted-foreground">
                  {person.email_address}
                </p>
              )}
            </div>
          ))}
        </div>
      </BaseTooltip>
    );
  },
);
PersonTooltip.displayName = "PersonTooltip";

// REVERTED ActivityTooltip - uses TooltipProps from Recharts
export const ActivityTooltip = ({ active, payload, label }: TooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  const timestamp = label as number;
  const exactDate = formatTimestampShort(timestamp);

  // Original simple rendering
  return (
    <div
      className="bg-card text-card-foreground p-3 shadow-lg rounded-lg border border-border backdrop-blur-sm backdrop-saturate-125 relative z-50"
      // Apply original inline styles if any
      style={{ transform: "translateY(20px)", marginTop: "10px" }}
    >
      <p className="text-sm font-medium">{exactDate}</p>
      {/* Add count/value logic here if needed, based on original implementation */}
    </div>
  );
};
// ActivityTooltip.displayName = "ActivityTooltip"; // Optional
